/**
 * Settings → Bot → Antwort-Kette.
 * Auto bleibt der Normalfall. Grok oder Gemini greifen wirklich —
 * kein totes Opus/Sonnet-Schild mehr.
 */
import {
  loadSettings,
  saveSettings,
  type ModellBevorzugt,
} from "../settings.js";
import { kontoGruppen } from "../konto-lage.js";
import { nameFuerModell } from "../../../shared/modell-namen.js";
import {
  GEMINI_BACKUPS,
  GEMINI_SCHNELL,
  GEMINI_STARK,
  geminiBackupAus,
} from "../../../server/models.js";

/*
 * Ticket #1883 (2026-08-22, Martin): "Ich dachte, das System arbeitet
 * ausschliesslich mit Claude und Gemini — Grok war mir nicht bewusst."
 * Davor mehrfach deutlicher: "Ich sehe immer noch diesen Scheiss Grok 4.6
 * in der Fusszeile!"
 *
 * Grok bleibt im Code als LETZTER Halt, wenn weder Claude noch Gemini
 * erreichbar sind — besser reden als schweigen. Aber es ist nichts mehr,
 * was man WAEHLT: Die beiden Knoepfe sind weg, und solange Gemini
 * antwortet, kommt Grok nicht mehr an die Reihe (siehe runtime-modell.ts).
 */
const WAHLEN: ReadonlyArray<{ id: ModellBevorzugt; label: string }> = [
  { id: "auto", label: "Auto" },
  { id: "gemini", label: "Gemini" },
];

function aktuelleWahl(): ModellBevorzugt {
  // Wer frueher Grok gewaehlt hatte, landet auf Auto — die Option gibt es
  // nicht mehr, und eine unsichtbare aktive Einstellung waere ein Geist.
  const v = loadSettings().modellBevorzugt;
  return v === "gemini" ? v : "auto";
}

function hinweisFuer(w: ModellBevorzugt): string {
  if (w === "gemini") {
    // 19.10.2026: vom Live-Anruf nur reden, wer ihn hat (shared/einstellungen-sichtbar.ts).
    return kontoGruppen().anrufModell ? "Immer Gemini. Der Live-Anruf bleibt Gemini Live." : "Immer Gemini.";
  }
  return "Auto: Claude-Abo zuerst. Ist das Kontingent tot, die Backup-API.";
}

/**
 * Bekannte Gemini-Backup-Stufen — aus dem Register, keine Kennung hier.
 * ⚠️ 29.09.2026 (Ticket 2793): Hier stand „gemini-3.7-flash". Die App verwarf
 *    den Wert beim Laden als unbekannt, die Wahl blieb 3.1 Pro.
 */
const BACKUP_TEXTE: Readonly<Record<string, string>> = {
  [GEMINI_STARK]: "Pro-Stufe — sorgfältiger, etwas langsamer",
  [GEMINI_SCHNELL]: "Flash-Stufe — deutlich schneller",
};

export const BACKUP_APIS: ReadonlyArray<{
  id: string;
  label: string;
  beschreibung: string;
}> = GEMINI_BACKUPS.map((id) => ({
  id,
  label: nameFuerModell(id),
  beschreibung: BACKUP_TEXTE[id] ?? "",
}));

/** Die gespeicherte Wahl, gelesen wie am Server (geminiBackupAus). */
export function aktuelleBackup(): string {
  return geminiBackupAus(loadSettings().fallbackModel);
}

function baueBackupZeile(): HTMLDetailsElement {
  const zeile = document.createElement("details");
  zeile.className = "settings-backup-zeile";
  const kopf = document.createElement("summary");
  kopf.textContent = `Backup-API · ${nameFuerId(aktuelleBackup())}`;
  const text = document.createElement("p");
  text.textContent =
    "Wenn das Claude-Abo tot ist, antwortet Gemini mit denselben Werkzeugen — Faktura, Kalender, Mail. Auto nimmt Claude zuerst.";
  zeile.append(kopf, text);
  return zeile;
}

export function createModellKetteCard(): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "settings-subsection";

  const title = document.createElement("h3");
  title.className = "settings-subsection-title";
  title.textContent = "Wer antwortet";
  wrap.appendChild(title);

  const lbl = document.createElement("div");
  lbl.className = "settings-radio-label";
  lbl.textContent = "Antworten mit";
  wrap.appendChild(lbl);

  const row = document.createElement("div");
  row.className = "settings-radio-row";
  wrap.appendChild(row);

  const hint = document.createElement("p");
  hint.className = "settings-modules-hint";
  wrap.appendChild(hint);

  const knoepfe: HTMLButtonElement[] = [];
  const markiere = (aktiv: ModellBevorzugt): void => {
    hint.textContent = hinweisFuer(aktiv);
    for (const b of knoepfe) {
      b.classList.toggle("is-active", b.dataset.value === aktiv);
    }
  };

  const box = document.createElement("div");
  box.className = "settings-model-status";
  const liste = document.createElement("ol");
  liste.className = "settings-model-kette";

  /*
   * 28.09.2026 (Ticket 2779): Für ein Kundenkonto antwortet Gemini im Paket.
   * Die Wahl Auto/Gemini und die Backup-Zeile gehören zum Claude-Abo des
   * Betreibers — dort gelten sie nicht, also stehen sie nicht da. Das sagt
   * der Server (`kunde`), nicht eine Annahme der App.
   */
  const backupZeile = baueBackupZeile();
  const alsKunde = (): void => {
    for (const el of [lbl, row, hint, backupZeile]) el.hidden = true;
  };

  for (const w of WAHLEN) {
    const opt = document.createElement("button");
    opt.type = "button";
    opt.className = "settings-radio-btn";
    opt.dataset.value = w.id;
    opt.textContent = w.label;
    opt.addEventListener("click", () => {
      const s = loadSettings();
      saveSettings({ ...s, modellBevorzugt: w.id });
      markiere(w.id);
      void zeichneStatus(box, liste, w.id, aktuelleBackup(), alsKunde);
    });
    row.appendChild(opt);
    knoepfe.push(opt);
  }

  wrap.append(backupZeile, box, liste);

  const start = aktuelleWahl();
  markiere(start);
  void zeichneStatus(box, liste, start, aktuelleBackup(), alsKunde);
  return wrap;
}

function zeichneStatus(
  box: HTMLElement,
  liste: HTMLElement,
  wahl: ModellBevorzugt,
  backup: string,
  alsKunde?: () => void,
): Promise<void> {
  box.replaceChildren();
  liste.replaceChildren();
  delete box.dataset.fallback;
  return fetch(
    `/api/runtime-modell?wahl=${encodeURIComponent(wahl)}&backup=${encodeURIComponent(backup)}`,
    {
      credentials: "include",
    },
  )
    .then((r) => (r.ok ? r.json() : null))
    .then(
      (j: {
        id?: string;
        fallback?: boolean;
        /** Kundenkonto: Gemini im Paket ist der Normalfall, kein Notfall. */
        kunde?: boolean;
        grund?: string | null;
        kette?: Array<{
          name: string;
          status: string;
          hinweis: string;
        }>;
      } | null) => {
        if (!j) {
          box.textContent = "Status gerade nicht lesbar.";
          return;
        }
        if (j.kunde) alsKunde?.();
        else if (j.fallback) box.dataset.fallback = "1";
        const dot = document.createElement("span");
        dot.className = "settings-model-status-dot";
        const text = document.createElement("div");
        text.className = "settings-model-status-text";
        const jetzt = document.createElement("strong");
        jetzt.textContent = nameFuerId(j.id ?? "");
        const sub = document.createElement("span");
        sub.textContent = j.grund
          ? `Jetzt · ${j.grund}`
          : "Jetzt · Claude-Abo erreichbar";
        text.appendChild(jetzt);
        text.appendChild(sub);
        box.appendChild(dot);
        box.appendChild(text);
        for (const st of j.kette ?? []) {
          const li = document.createElement("li");
          li.dataset.status = st.status;
          const kopf = document.createElement("strong");
          kopf.textContent = st.name;
          const z = document.createElement("span");
          z.textContent = `${statusWort(st.status)} — ${st.hinweis}`;
          li.appendChild(kopf);
          li.appendChild(z);
          liste.appendChild(li);
        }
      },
    )
    .catch(() => {
      box.textContent = "Status gerade nicht lesbar.";
    });
}

/**
 * 2026-08-20: "Grok 4" war für BEIDE Stufen gleich — 4.5 (schnell) und 4.6.
 * Die Zeile heißt "Jetzt", also muss sie sagen, wer jetzt wirklich spricht.
 *
 * ⚠️ 14.09.2026: Genau das galt für Grok, aber NICHT für Gemini — dessen
 * Name stand hier fest verdrahtet. Als die Kennung im Register auf
 * `gemini-3.8-flash` wechselte (pre.2869), zeigte die App weiter "3.7".
 * Die Ableitung steht jetzt einmal in shared/modell-namen.ts, für App UND
 * Server.
 */
export function nameFuerId(id: string): string {
  if (/claude-cli-warm-opus/i.test(id)) return "Opus · Abo";
  return nameFuerModell(id);
}

/**
 * Läuft gerade ein Ersatz statt des Claude-Abos? Für die Fußzeile (Amber).
 *
 * 2026-07-05 (Martin): Amber-Warnung, wenn NICHT auf dem Claude-Abo — damit
 * man sofort sieht, dass man herabgestuft läuft. Die Gemini-Schnellspur
 * („…-schnell“) ist Absicht, kein Ersatz.
 * ⚠️ 28.09.2026 (Ticket 2779): Für ein Kundenkonto ist Gemini im Paket der
 *    Normalfall. „Claude-Kontingent verbraucht“ wäre dort schlicht falsch.
 */
export function istErsatzModell(model: string, kunde: boolean): boolean {
  if (kunde) return false;
  return /gemini|gpt-|grok/i.test(model) && !/schnell/i.test(model);
}

/**
 * Der Hinweis am Modell-Chip, wenn ein Ersatz antwortet. (29.09.2026,
 * Ticket 2782)
 *
 * ⚠️ Hier stand fest „Claude-Kontingent verbraucht, nicht gekündigt“ —
 *    auch wenn das Abo nur überlastet war oder die Anmeldung fehlte. Der
 *    Grund kommt jetzt vom Server (/api/runtime-modell, `grund`): derselbe
 *    Satz wie in „Wer antwortet“, mit „ruht bis …“.
 */
export function ersatzTitel(label: string, grund: string | null): string {
  return `Fallback aktiv: ${label} · ${grund ?? "das Claude-Abo antwortet gerade nicht"}`;
}

/**
 * Der Hinweis am Chip, wenn das Abo antwortet — ruht dabei ein Modell, steht
 * es darunter (Prüfbefund zu Ticket 2782): „Opus 5.5 überlastet — ruht bis
 * …, Sonnet 5 übernimmt“. Ohne ruhendes Modell bleibt der Hinweis, wie er ist.
 */
export function aboTitel(basis: string, stufe: string | null): string {
  return stufe ? `${basis}\n${stufe}` : basis;
}

/**
 * Der Satz oben im Blatt „Was antwortet“, das ein Tippen auf den Chip
 * öffnet. (Prüfbefund zu Ticket 2782)
 *
 * ⚠️ Der Hinweis beim Darüberfahren (title) ist am iPhone unsichtbar. Dort
 *    stand fest „Claude-Abo tot — Gemini mit Werkzeugen.“ — auch bei einer
 *    Überlastung, und nie, bis wann. Jetzt derselbe Grund wie am Chip.
 *
 * @param ersatz Antwortet gerade ein Ersatz statt des Abos?
 * @param grund  Der Satz des Servers (/api/runtime-modell, `grund`) oder null.
 */
export function blattHinweis(ersatz: boolean, grund: string | null): string {
  if (ersatz) {
    return grund
      ? `${grund} · Gemini mit Werkzeugen.`
      : "Claude-Abo antwortet gerade nicht — Gemini mit Werkzeugen.";
  }
  return grund
    ? `Läuft über dein Abo · ${grund}.`
    : "Läuft über dein Abo — kein API-Guthaben.";
}

function statusWort(s: string): string {
  switch (s) {
    case "aktiv":
      return "jetzt";
    case "bereit":
      return "bereit";
    case "tot":
      // Ticket 2782: Warum und bis wann, sagt der Hinweis dahinter — das
      // Abo ruht auch bei Überlastung, und eine tote Anmeldung ist kein
      // Kontingent.
      return "aus";
    case "kein-guthaben":
      return "kein Guthaben";
    case "fehlt":
      return "nicht eingerichtet";
    default:
      return s;
  }
}
